import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (mirrors study_companion/mood_tracker/recipe_box +
// the finance-fence shape from oriora_analyst). askBrain sends the message + this app's declared
// action catalog to the generic fleet chat brain (POST /api/apps/oriora_statements/chat) — ONE
// billed call returns ONE registered action + a reply; this file resolves merchant/category text
// references locally (the data lives here) and the app executes the action on-device. The
// original on-device regex router below (routeMessage) is kept as the FREE FALLBACK for offline /
// signed-out / out-of-funds — chat never goes dead. Original prototype notes:
//   • "recategorize X as Y" / "set X category" → resolve locally               → recategorize
//   • "delete / remove <merchant>"             → resolve id locally           → delete_txn (UI confirms)
//   • "show / find / list / search <term>"     → local listTxns filter        → filter
//   • "recap / summarise / overview …"          → existing /recap  (draftRecap) → recap (billed)
//   • a spending question ("how much …?")       → existing /answer (answerQuestion) → ask (billed)
//   • anything else                             → none + a short clarifying nudge
//
// FINANCE-DATA FENCE (HARD, MANDATORY pre-LLM on BOTH paths). Oriora Statements DESCRIBES the
// user's own statement data; it NEVER gives investment, budgeting, tax, or financial advice. The
// fence is the existing Rust `advice_check` (invoked via store.adviceCheck) — already enforced
// server-side in build_query_slice AND client-side inside executeAction (ask/recap). This file
// adds a THIRD layer: it runs store.adviceCheck() on the raw message BEFORE the (billed) routing
// call is ever made, so an advice-seeking ask is refused for FREE before it can reach EITHER
// billed call (routing OR answer/recap) — mirrors academicRefusal/healthRefusal/adviceRefusal in
// study_companion/recipe_box/oriora_analyst. STRICTER THAN TEMPLATE (like oriora_analyst): for
// the two billed 'ask'/'recap'-type actions, the routing model's own freeform "reply" text is
// DISCARDED — the visible answer always comes from the strictly-fenced /answer or /recap
// endpoint (executeAction), never from this unfenced routing call.

import { store, CATEGORIES, categoryLabel, fmtMinor, type Txn, type Category } from "../store";
import { BrainError, brainCall } from "../brain";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, FilterRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // FINANCE FENCE: always "" here — the model's freeform reply is discarded.
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (defense-in-depth ON TOP OF advice_check + reply-discard).
const SCOPE =
  "Hard limits: never give investment, budgeting, tax, or financial advice or recommendations, never " +
  "predict or forecast, never say whether the user can afford something or should buy/sell/invest/switch — " +
  "decline plainly. This app DESCRIBES the user's OWN statement data: search/recategorize/delete transactions " +
  "and give an OBSERVATIONAL recap/answer about what they actually spent; back up to their own cloud.";

const HELP =
  'I can answer questions about your statements, recap a period, search, recategorize, and delete — just tell me. Try:\n' +
  '• "how much did I spend on groceries last month?"\n' +
  '• "recap last month"\n' +
  '• "show Amazon"\n' +
  '• "recategorize Uber as transport"\n' +
  '• "delete Starbucks"';

// A spending question that warrants the (billed) /answer call.
const ASK_RE = /(\?|\bhow\s+much\b|\bhow\s+many\b|\bwhat\b|\bwhich\b|\bwhen\b|\bwhy\b|\bspend\b|\bspent\b|\bpaid\b|\bpay\b|\bcost\b|\bincome\b|\btotal\b|\baverage\b|\btop\b|\bmerchants?\b)/i;

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

async function findByMerchant(term: string): Promise<Txn[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listTxns({ limit: 5000 });
  const subs = all.filter((t) => t.merchant.toLowerCase().includes(q) || t.raw_description.toLowerCase().includes(q));
  if (subs.length) return subs;
  // Fleet FUZZY standard (fuzzy.ts): forgiving near-match on the merchant name so a mistyped/
  // paraphrased merchant still resolves ("starbcks" → "Starbucks"). Client-side; data on-device.
  const named = findNamedMatches(all.map((t) => ({ ...t, name: t.merchant })), term);
  return named.map(({ name: _n, ...t }) => t as Txn);
}
/** "Couldn't find X" enriched with the closest merchant names ("did you mean …?"). */
async function merchantNotFound(term: string): Promise<string> {
  const all = await store.listTxns({ limit: 5000 });
  return notFoundMessage(all.map((t) => ({ name: t.merchant })), term);
}

function resolveCategory(name: string): Category | null {
  const q = name.trim().toLowerCase().replace(/[.?!]+$/, "");
  const hit = CATEGORIES.find((c) => c === q || categoryLabel(c).toLowerCase() === q || categoryLabel(c).toLowerCase().startsWith(q));
  return hit ?? null;
}

function pickList(rows: Txn[]): string {
  return rows.slice(0, 5).map((t) => `• ${t.merchant} — ${fmtMinor(t.amount, t.currency)} (${t.posted_on})`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  // FINANCE-DATA FENCE — pre-LLM, free, on BOTH paths. An advice-seeking ask is refused
  // deterministically before it can reach EITHER billed call (routing OR answer/recap).
  const refusal = await store.adviceCheck(text);
  if (refusal) { const r = none(refusal); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (merchant/category text → id) locally,
// because the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.statements.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  {
    name: "ask",
    description:
      "Answer a factual, DESCRIPTIVE question about the user's own spending/income from their statements (amounts, merchants, categories, trends). NEVER use this for budgeting, investment, tax, or advice-seeking questions ('should I…', 'how can I save', 'is this a good…'); if the user is asking for advice rather than a description of their data, do not pick this action.",
    params: { question: "the user's question, verbatim" },
  },
  {
    name: "recap",
    description: "Give a neutral, factual recap/summary of spending and income for a period (e.g. last month). Never advice.",
    params: { question: "the period/recap request, verbatim" },
  },
  { name: "filter", description: "Search / list the user's transactions by merchant/description text, or by category.", params: { term: "optional free text to match a merchant/description", category: `optional category, one of: ${CATEGORIES.join(", ")}` } },
  { name: "recategorize", description: "Change one transaction's category, identified by merchant text.", params: { merchant: "which transaction — merchant text as the user wrote it", category: `new category, one of: ${CATEGORIES.join(", ")}` } },
  { name: "delete_txn", description: "Delete one transaction identified by merchant text (the app asks the user to confirm first).", params: { merchant: "which transaction — merchant text as the user wrote it" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole statement data — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = `LIBRARY (their saved plans/notes): ${librarySummary()}. Categories: ${CATEGORIES.join(", ")}.` + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  // FINANCE FENCE: never surface the model's freeform reply — reply stays "" (see toAction).
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: HELP }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, text);
    actions.push(one.action);
  }
  return { reply: "", actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

export async function toAction(r: ServerChat, userText: string): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "ask": {
      // FINANCE FENCE — STRICTER THAN TEMPLATE (mirrors oriora_analyst): deliberately DISCARD
      // `say` — the visible answer for a spending question must come ONLY from the strictly-
      // fenced /answer endpoint (executeAction → answerQuestion), never from this unfenced
      // routing call's own text.
      const question = asStr(p.question) ?? userText;
      return { reply: "", action: { kind: "ask", question } };
    }
    case "recap": {
      // Same discard-freeform-text discipline as "ask" above — /recap is the fenced source.
      const question = asStr(p.question) ?? userText;
      return { reply: "", action: { kind: "recap", question } };
    }
    case "filter": {
      const term = asStr(p.term);
      const catStr = asStr(p.category);
      const category = catStr ? resolveCategory(catStr) ?? undefined : undefined;
      return { reply: "", action: { kind: "filter", term, category, label: say || term || (category ? categoryLabel(category) : undefined) } };
    }
    case "recategorize": {
      const merchantTerm = asStr(p.merchant);
      const catStr = asStr(p.category);
      if (!merchantTerm) return none(say || "Which transaction do you want to recategorize? Give me the merchant name.");
      const cat = catStr ? resolveCategory(catStr) : null;
      if (!cat) return none(`"${catStr ?? ""}" isn't a category. Use one of: ${CATEGORIES.join(", ")}.`);
      const rows = await findByMerchant(merchantTerm);
      if (rows.length === 0) return none(await merchantNotFound(merchantTerm));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${merchantTerm}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "recategorize", id: rows[0].id, category: cat, label: rows[0].merchant } };
    }
    case "delete_txn": {
      const merchantTerm = asStr(p.merchant);
      if (!merchantTerm) return none(say || "Which transaction do you want to delete? Give me the merchant name.");
      if (/^accounts?\b/i.test(merchantTerm)) return none("Accounts are removed from the Accounts panel, not the chat.");
      const rows = await findByMerchant(merchantTerm);
      if (rows.length === 0) return none(await merchantNotFound(merchantTerm));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${merchantTerm}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "delete_txn", id: rows[0].id, label: `${rows[0].merchant} (${fmtMinor(rows[0].amount, rows[0].currency)})` } };
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 0) The finance-data fence runs FIRST — an advice-seeking ask never reaches any branch.
  const refusal = await store.adviceCheck(t);
  if (refusal) return none(refusal);

  // 1) RECATEGORIZE — "recategorize X as Y" / "categorize X as Y" / "set X's category to Y".
  let m = t.match(/^(?:re)?categori[sz]e\s+(.+?)\s+as\s+(.+)$/i)
    || t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+categor(?:y|ies)\s+to\s+(.+)$/i);
  if (m) {
    const cat = resolveCategory(m[2]);
    if (!cat) return none(`"${m[2].trim()}" isn't a category. Use one of: ${CATEGORIES.join(", ")}.`);
    const rows = await findByMerchant(m[1]);
    if (rows.length === 0) return none(await merchantNotFound(m[1]));
    if (rows.length > 1) return none(`I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", action: { kind: "recategorize", id: rows[0].id, category: cat, label: rows[0].merchant } };
  }

  // 2) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  m = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:transaction\s+|txn\s+)?(?:for\s+|from\s+)?([\s\S]+)$/i);
  if (m) {
    const term = m[1].trim();
    if (/^accounts?\b/i.test(term)) return none("Accounts are removed from the Accounts panel, not the chat.");
    const rows = await findByMerchant(term);
    if (rows.length === 0) return none(await merchantNotFound(term));
    if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", action: { kind: "delete_txn", id: rows[0].id, label: `${rows[0].merchant} (${fmtMinor(rows[0].amount, rows[0].currency)})` } };
  }

  // 3) RECAP — neutral period recap via the existing /recap endpoint (billed).
  if (/\b(recap|summar(?:y|ise|ize)|overview)\b/i.test(low)) {
    return { reply: "", action: { kind: "recap", question: t } };
  }

  // 4) FILTER / SEARCH — local, free listing by merchant/description.
  m = t.match(/^(?:show|find|list|search)\s+(.+)$/i);
  if (m) {
    let term = m[1].trim().replace(/^(?:me\s+)?(?:all\s+)?(?:my\s+)?/i, "").replace(/\b(?:transactions?|txns?|payments?)\b/gi, "").replace(/^(?:for|from)\s+/i, "").trim();
    const cat = resolveCategory(term);
    if (cat && categoryLabel(cat).toLowerCase() === term.toLowerCase()) {
      return { reply: "", action: { kind: "filter", category: cat, label: categoryLabel(cat) } };
    }
    if (!term) return none("What should I search for? e.g. \"show Amazon\".");
    return { reply: "", action: { kind: "filter", term, label: term } };
  }

  // 5) ASK — a spending question → existing /answer endpoint (billed).
  if (ASK_RE.test(low)) {
    return { reply: "", action: { kind: "ask", question: t } };
  }

  // 6) Unclear → a short clarifying nudge (no billed call).
  return none(HELP);
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
